<template>
  <div data-testid="lead-request-history">
    <h3 class="mb-4 txt-headline-m">{{ t('LeadDetail.requestHistory.title', { count: requests.length }) }}</h3>
    <div class="overflow-x-auto">
      <TableLight class="w-full table-fixed">
        <thead>
          <tr>
            <TableH class="w-48" :label="t('LeadDetail.field.type')" />
            <TableH class="w-44 whitespace-nowrap" :label="t('LeadDetail.field.receivedAt')" />
            <TableH :label="t('LeadDetail.field.message')" />
          </tr>
        </thead>
        <tbody>
          <tr v-for="request in visibleRequests" :key="request.id ?? ''" :data-testid="`lead-request-${request.id}`">
            <td class="text-left align-top">
              <ChipBadge v-if="request.contactRequestType" :color="badgeColorByType[request.contactRequestType]" rounded>
                {{ t(`Leads.type.${request.contactRequestType}`) }}
              </ChipBadge>
              <span v-else>---</span>
            </td>
            <td class="text-left align-top whitespace-nowrap">
              <time v-if="request.createdAt" :datetime="request.createdAt">{{ d(request.createdAt, 'dateTimeShort') }}</time>
              <span v-else>---</span>
            </td>
            <td class="text-left align-top">
              <div v-if="request.locked" class="flex items-center gap-2" :data-testid="`lead-request-${request.id}-locked`">
                <LockIcon class="lucide-icon-s shrink-0 text-icon-primary" />
                <span class="text-subtle">{{ t('LeadDetail.requestHistory.lockedHint') }}</span>
              </div>
              <p v-else-if="request.message" class="break-words whitespace-pre-line" :data-testid="`lead-request-${request.id}-message`">
                {{ request.message }}
              </p>
              <span v-else>---</span>
            </td>
          </tr>
        </tbody>
      </TableLight>
    </div>
    <ShowMoreToggle
      v-if="hiddenCount > 0"
      v-model:expanded="expanded"
      class="mt-4"
      :more-label="t('LeadDetail.requestHistory.showMore', { count: hiddenCount })"
      :less-label="t('LeadDetail.requestHistory.showLess')"
      data-testid="lead-requests-toggle"
    />
  </div>
</template>

<script setup lang="ts">
import { ChipBadge } from '@ramp106/omrjs-core-ui'
import { LockIcon } from '@ramp106/omrjs-core-ui/icons'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import ShowMoreToggle from '@/ui/ShowMoreToggle.vue'
import { TableH, TableLight } from '@/ui/TableLight'

import { badgeColorByType } from './leadType'
import type { LeadRequest } from './types'

const VISIBLE_REQUEST_LIMIT = 10

const props = defineProps<{
  requests: LeadRequest[]
}>()

const { t, d } = useI18n()

const expanded = ref(false)

const sortedRequests = computed(() =>
  [...props.requests].sort((a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime()),
)

const visibleRequests = computed(() => (expanded.value ? sortedRequests.value : sortedRequests.value.slice(0, VISIBLE_REQUEST_LIMIT)))
const hiddenCount = computed(() => Math.max(0, sortedRequests.value.length - VISIBLE_REQUEST_LIMIT))
</script>
